import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, AlertCircle, Info, CheckCircle, XCircle, LucideMailCheck, ExternalLink } from 'lucide-react';
import { useGenerationStore } from '../../store/generation/generationStore';

const NotificationPanel = ({ isOpen, onClose, notifications, onMarkAsRead, onMarkAllAsRead }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'critical', 'info'
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const handleNotificationClick = (notification) => {
    if (notification.actionUrl) {
      onMarkAsRead(notification.notificationId);
      onClose();

      // For project-chat routes, build navigation state from generation store
      if (notification.actionUrl.includes('/project-chat/')) {
        const session = useGenerationStore.getState().activeScriptGeneration;
        if (session) {
          navigate(notification.actionUrl, {
            state: {
              projectData: session.projectData,
              conversationId: session.conversationId,
              fromProjectView: session.fromProjectView,
              loadExistingMessages: true,
              isTestSuiteMode: session.isTestSuiteMode,
              existingProjectId: session.existingProjectId,
              existingProjectName: session.existingProjectName
            }
          });
          return;
        }
      }

      navigate(notification.actionUrl);
    }
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Filter notifications based on selected filter and read status
  const filteredNotifications = notifications.filter(notification => {
    // Only show unread notifications
    if (notification.read) return false;
    
    if (filter === 'all') return true;
    if (filter === 'critical') {
      return notification.type?.toUpperCase() === 'ERROR' || 
             notification.type?.toUpperCase() === 'FAILURE' ||
             notification.type?.toUpperCase() === 'WARNING' || 
             notification.type?.toUpperCase() === 'CRITICAL';
    }
    if (filter === 'info') {
      return notification.type?.toUpperCase() === 'SUCCESS' || 
             notification.type?.toUpperCase() === 'INFO';
    }
    return true;
  });

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'ERROR':
      case 'FAILURE':
        return <XCircle size={20} className="notification-icon error" />;
      case 'WARNING':
        return <AlertTriangle size={20} className="notification-icon warning" />;
      case 'SUCCESS':
        return <CheckCircle size={20} className="notification-icon success" />;
      case 'CRITICAL':
        return <AlertCircle size={20} className="notification-icon critical" />;
      case 'INFO':
      default:
        return <Info size={20} className="notification-icon info" />;
    }
  };

  // Format time for display with proper timezone handling
  const formatTime = (date, time) => {
    try {
      // Parse the backend time as UTC (this gives us the correct time based on your logs)
      const dateTimeString = `${date}T${time.split('.')[0]}Z`; // Add Z to treat as UTC
      const utcDateTime = new Date(dateTimeString);
      
      // Get current time
      const now = new Date();
      
      // Calculate the difference in milliseconds
      const diffInMs = now.getTime() - utcDateTime.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      
      // Format the relative time using your original logic
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
      
    } catch (error) {
      return 'Recently';
    }
  };

  // Get unread count for each filter
  const getFilterCount = (filterType) => {
    if (filterType === 'all') {
      return notifications.filter(n => !n.read).length;
    }
    if (filterType === 'critical') {
      return notifications.filter(n => !n.read && (
        n.type?.toUpperCase() === 'ERROR' || 
        n.type?.toUpperCase() === 'FAILURE' ||
        n.type?.toUpperCase() === 'WARNING' || 
        n.type?.toUpperCase() === 'CRITICAL'
      )).length;
    }
    if (filterType === 'info') {
      return notifications.filter(n => !n.read && (
        n.type?.toUpperCase() === 'SUCCESS' || 
        n.type?.toUpperCase() === 'INFO'
      )).length;
    }
    return 0;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="notification-panel-overlay">
      <div 
        ref={panelRef}
        className={`notification-panel ${isOpen ? 'open' : ''}`}
      >
        {/* Header */}
        <div className="notification-panel-header">
          <div className="notification-panel-title">
            <h3>Notifications</h3>
            <span className="notification-count">
              {filteredNotifications.length}
            </span>
          </div>
          <div className="notification-panel-actions">
            {filteredNotifications.length > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={onMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
            <button 
              className="close-panel-btn"
              onClick={onClose}
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="notification-filters">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
            {getFilterCount('all') > 0 && (
              <span className="filter-count">{getFilterCount('all')}</span>
            )}
          </button>
          <button 
            className={`filter-tab ${filter === 'critical' ? 'active' : ''}`}
            onClick={() => setFilter('critical')}
          >
            Critical
            {getFilterCount('critical') > 0 && (
              <span className="filter-count critical">{getFilterCount('critical')}</span>
            )}
          </button>
          <button 
            className={`filter-tab ${filter === 'info' ? 'active' : ''}`}
            onClick={() => setFilter('info')}
          >
            Info
            {getFilterCount('info') > 0 && (
              <span className="filter-count">{getFilterCount('info')}</span>
            )}
          </button>
        </div>

        {/* Notifications List */}
        <div className="notification-list">
          {filteredNotifications.length === 0 ? (
            <div className="no-notifications">
              <div className="no-notifications-icon">
                <CheckCircle size={48} />
              </div>
              <h4>All caught up!</h4>
              <p>No {filter === 'all' ? '' : filter} notifications to show.</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.notificationId}
                className={`notification-item ${notification.type.toLowerCase()} ${notification.actionUrl ? 'clickable' : ''}`}
                onClick={notification.actionUrl ? () => handleNotificationClick(notification) : undefined}
                style={notification.actionUrl ? { cursor: 'pointer' } : undefined}
              >
                {getNotificationIcon(notification.type)}
                <div className="notification-item-content">
                  <div className="notification-item-header">
                    <div className="notification-item-main">
                      <h4 className="notification-title">{notification.title}</h4>
                      <span className="notification-time">
                        {formatTime(notification.date, notification.time)}
                      </span>
                    </div>
                    <button
                      className="mark-read-btn"
                      onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.notificationId); }}
                      title="Mark as read"
                    >
                      <LucideMailCheck size={16} />
                    </button>
                  </div>
                  <p className="notification-description">
                    {notification.description}
                  </p>
                  {notification.actionUrl && (
                    <span className="notification-action-link">
                      <ExternalLink size={12} />
                      Click to view
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

NotificationPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      notificationId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      actionUrl: PropTypes.string,
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      read: PropTypes.bool
    })
  ).isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
  onMarkAllAsRead: PropTypes.func.isRequired
};

export default NotificationPanel;